import { Box, Text, Skeleton } from "@mantine/core";
import type { CatalogProvider } from "@hooks/flow/useModelCatalog";
import type { LLMConfigOptionsViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { ProviderModelSelect } from "../ProviderModelSelect";
import { ConfigOptionsFields } from "../ConfigOptionsFields";
import { RuntimeConfigOptionsFields } from "../RuntimeConfigOptionsFields";
import "./LLMConfigTab.scss";

interface LLMConfigTabProps {
    stepType: string;
    isLoadingProviders: boolean;
    isLoadingModels?: boolean;
    providers: CatalogProvider[];
    models: string[];
    selectedProvider: string;
    modelName: string;
    temperature: number | string;
    topP: number | string;
    topK: number | string;
    maxOutputTokens: number | string;
    thinkingBudget: number | string;
    seed: number | string;
    configOptions: LLMConfigOptionsViewModel | null;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
    onTemperatureChange: (value: number | string) => void;
    onTopPChange: (value: number | string) => void;
    onTopKChange: (value: number | string) => void;
    onMaxOutputTokensChange: (value: number | string) => void;
    onThinkingBudgetChange: (value: number | string) => void;
    onSeedChange: (value: number | string) => void;
    onConfigOptionsChange: (value: LLMConfigOptionsViewModel | null) => void;
    onConfigOptionsDirty?: () => void;
}

export const LLMConfigTab = ({
    stepType,
    isLoadingProviders,
    isLoadingModels,
    providers,
    models,
    selectedProvider,
    modelName,
    temperature,
    topP,
    topK,
    maxOutputTokens,
    thinkingBudget,
    seed,
    configOptions,
    onProviderChange,
    onModelChange,
    onTemperatureChange,
    onTopPChange,
    onTopKChange,
    onMaxOutputTokensChange,
    onThinkingBudgetChange,
    onSeedChange,
    onConfigOptionsChange,
    onConfigOptionsDirty,
}: LLMConfigTabProps) => {
    const { t } = useTranslation();

    if (isLoadingProviders) {
        return (
            <Box className="llm-config-tab__form">
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={120} />
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={56} />
            </Box>
        );
    }

    return (
        <Box className="llm-config-tab__form">
            <ProviderModelSelect
                isLoadingProviders={isLoadingProviders}
                isLoadingModels={isLoadingModels}
                providers={providers}
                models={models}
                selectedProvider={selectedProvider}
                modelName={modelName}
                onProviderChange={onProviderChange}
                onModelChange={onModelChange}
            />

            <Text size="sm" fw={600} className="llm-config-tab__subtitle">
                {t("admin.flowConfigs.labels.llmConfig")}
            </Text>

            <ConfigOptionsFields
                temperature={temperature}
                topP={topP}
                topK={topK}
                maxOutputTokens={maxOutputTokens}
                thinkingBudget={thinkingBudget}
                seed={seed}
                onTemperatureChange={onTemperatureChange}
                onTopPChange={onTopPChange}
                onTopKChange={onTopKChange}
                onMaxOutputTokensChange={onMaxOutputTokensChange}
                onThinkingBudgetChange={onThinkingBudgetChange}
                onSeedChange={onSeedChange}
            />

            <RuntimeConfigOptionsFields
                stepType={stepType}
                value={configOptions}
                onChange={onConfigOptionsChange}
                onDirty={onConfigOptionsDirty}
            />
        </Box>
    );
};
